export class HighlightManager {
  constructor(config = {}) {
    this.config = {
      highlightClass: 'pixl-highlight',
      highlightTag: 'mark',
      ...config
    };
    
    this.originalContent = new WeakMap();
  }

  highlightMatches(elements, searchQuery) {
    if (!searchQuery || searchQuery.trim().length === 0) {
      this.clearHighlights(elements);
      return;
    }

    elements.forEach((match) => {
      // Use the specific search term that matched this element
      const termToHighlight = match.searchTerm || searchQuery;
      this.highlightElement(match.element, termToHighlight, match.matchType);
    });
  }

  highlightElement(element, searchQuery, matchType = 'phrase') {
    if (!this.originalContent.has(element)) {
      this.originalContent.set(element, element.innerHTML);
    }

    const original = this.originalContent.get(element);
    const highlighted = this.createHighlightedHTML(original, searchQuery, matchType);
    
    if (element.innerHTML !== highlighted) {
      element.innerHTML = highlighted;
    }
  }

  createHighlightedHTML(html, searchQuery, matchType = 'phrase') {
    if (!searchQuery || searchQuery.trim().length === 0) return html;

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    if (matchType === 'phrase') {
      // For phrase matches, highlight the entire phrase
      this.highlightTextNodes(tempDiv, [searchQuery]);
    } else {
      // For word matches, highlight individual significant words
      const words = searchQuery.trim().split(/\s+/).filter(w => w.length > 2);
      this.highlightTextNodes(tempDiv, words);
    }
    
    return tempDiv.innerHTML;
  }

  highlightTextNodes(element, words) {
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (parent && (parent.tagName === 'SCRIPT' || 
                        parent.tagName === 'STYLE' || 
                        parent.tagName === this.config.highlightTag.toUpperCase())) {
            return NodeFilter.FILTER_REJECT;
          }
          return node.textContent.trim().length > 0 
            ? NodeFilter.FILTER_ACCEPT 
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    const textNodes = [];
    let node;
    
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }

    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      let highlightedText = text;
      
      words.forEach(word => {
        const escapedWord = this.escapeRegExp(word);
        const regex = new RegExp(`(${escapedWord})`, 'gi');
        
        highlightedText = highlightedText.replace(regex, (match) => {
          return `<${this.config.highlightTag} class="${this.config.highlightClass}">${match}</${this.config.highlightTag}>`;
        });
      });
      
      if (highlightedText !== text) {
        const span = document.createElement('span');
        span.innerHTML = highlightedText;
        textNode.parentNode.replaceChild(span, textNode);
        
        while (span.firstChild) {
          span.parentNode.insertBefore(span.firstChild, span);
        }
        span.parentNode.removeChild(span);
      }
    });
  }

  clearHighlights(elements) {
    if (!elements) {
      document.querySelectorAll(`[pixl-search-field]`).forEach(element => {
        if (this.originalContent.has(element)) {
          element.innerHTML = this.originalContent.get(element);
        }
      });
      return;
    }

    elements.forEach(element => {
      if (element.element) element = element.element;
      
      if (this.originalContent.has(element)) {
        element.innerHTML = this.originalContent.get(element);
      }
    });
  }

  clearAllHighlights() {
    document.querySelectorAll(`.${this.config.highlightClass}`).forEach(highlight => {
      const parent = highlight.parentNode;
      while (highlight.firstChild) {
        parent.insertBefore(highlight.firstChild, highlight);
      }
      parent.removeChild(highlight);
    });
  }

  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  updateHighlightClass(newClass) {
    this.config.highlightClass = newClass;
  }
}